import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faClock, faMapMarkerAlt, faCalendarPlus } from '@fortawesome/free-solid-svg-icons';

const ContactSection = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    serviceNeeded: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const serviceId = 'service_4yb8pi3';
    const publicKey = 'qcL6syXGHQM3tkOt8';
    const contactTemplateId = 'template_9mlx4cf';
    const autoReplyTemplateId = 'template_rk2z8gh';

    // Send notification to sharpdealmiami@gmail.com
    emailjs.sendForm(serviceId, contactTemplateId, formRef.current, publicKey, {
      subject: `New Appointment Request from ${formData.name || 'Unknown'}`
    })
      .then((result) => {
        console.log('Notification sent:', result.text);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          preferredDate: '',
          preferredTime: '',
          serviceNeeded: '',
          message: ''
        });
      })
      .catch((error) => {
        console.error('Notification error:', error.text);
        setSubmitStatus('error');
      });

    // Send auto-reply to user
    emailjs.sendForm(serviceId, autoReplyTemplateId, formRef.current, publicKey, {
      subject: 'Appointment Request Received - Sharp Deal Auto Repair',
      from_name: formData.name || 'Customer',
      current_date: new Date().toLocaleDateString(),
      from_email: formData.email // Ensure this is passed to the auto-reply
    })
      .then((result) => {
        console.log('Auto-reply sent:', result.text);
      })
      .catch((error) => {
        console.error('Auto-reply error:', error.text);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const serviceOptions = [
    { value: '', label: 'Select a Service' },
    { value: 'Brake Services', label: 'Brake Services' },
    { value: 'Engine Diagnostics', label: 'Engine Diagnostics' },
    { value: 'Transmission Repair', label: 'Transmission Repair' },
    { value: 'AC Services', label: 'AC Services' },
    { value: 'Electrical Systems', label: 'Electrical Systems' },
    { value: 'Oil Changes', label: 'Oil Changes' },
    { value: 'Other Services', label: 'Other Services' }
  ];

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faPhone} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Phone</h3>
                  <a href="tel:3058210101" className="text-primary hover:text-secondary font-medium">(305) 821-0101</a>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faClock} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Business Hours</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>Sunday: Closed</div>
                    <div>Monday: 8:30 AM - 6:00 PM</div>
                    <div>Tuesday: 8:30 AM - 6:00 PM</div>
                    <div>Wednesday: 8:30 AM - 6:00 PM</div>
                    <div>Thursday: 8:30 AM - 6:00 PM</div>
                    <div>Friday: 8:30 AM - 6:00 PM</div>
                    <div>Saturday: 8:30 AM - 12:00 PM</div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Location</h3>
                  <a href="https://maps.app.goo.gl/guq1ytqNJ1h3maVV9" className="text-primary hover:text-secondary font-medium" target="_blank" rel="noopener noreferrer">
                    1730 W 31st Pl<br />Hialeah, FL 33012
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Book Appointment</h2>
            {submitStatus === 'success' && <p className="text-green-600 mb-4">Your appointment request has been successfully submitted! We will contact you soon.</p>}
            {submitStatus === 'error' && <p className="text-red-600 mb-4">Error sending request. Please try again.</p>}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="John Doe" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="john@example.com" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="(305) 821-0101" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                  <input type="date" id="preferredDate" name="preferredDate" required value={formData.preferredDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
                </div>
                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                  <select id="preferredTime" name="preferredTime" required value={formData.preferredTime} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                    <option value="">Select Time</option>
                    <option value="08:30">8:30 AM</option>
                    <option value="09:00">9:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="14:00">2:00 PM</option>
                    <option value="15:00">3:00 PM</option>
                    <option value="16:00">4:00 PM</option>
                    <option value="17:00">5:00 PM</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="serviceNeeded" className="block text-sm font-medium text-gray-700 mb-2">Service Needed *</label>
                <select id="serviceNeeded" name="serviceNeeded" required value={formData.serviceNeeded} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                  {serviceOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Additional Notes (Optional)</label>
                <textarea id="message" name="message" rows="3" value={formData.message} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Describe any additional details..."></textarea>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                <FontAwesomeIcon icon={faCalendarPlus} className="mr-2" />
                {isSubmitting ? 'Sending...' : 'Schedule Appointment'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;